import React from 'react';
import { IFaucetConfig } from '../../../common/FaucetConfig';
import { FaucetCaptcha } from '../../shared/FaucetCaptcha';

import './GithubLogin.css';
import { toQuery } from '../../../utils/QueryUtils';
import { TypedEmitter } from 'tiny-typed-emitter';
import { FaucetTime } from '../../../common/FaucetTime';
import { IFaucetContext } from '../../../common/FaucetContext';

export interface IGithubLoginProps {
  faucetContext: IFaucetContext;
  faucetConfig: IFaucetConfig
}

export interface IGithubLoginState {
  popupOpen: boolean;
  authInfo: IGithubAuthInfo;
}

export interface IGithubAuthInfo {
  time: number;
  uid: number;
  user: string;
  url: string;
  avatar: string;
  token: string;
}

export class GithubLogin extends React.PureComponent<IGithubLoginProps, IGithubLoginState> {
  private messageEvtListener: (evt: MessageEvent) => void;
  private loginPopop: Window;

  constructor(props: IGithubLoginProps) {
    super(props);

    this.messageEvtListener = (evt: MessageEvent) => this.processWindowMessage(evt);

    this.state = {
      popupOpen: false,
      authInfo: null,
		};
  }

  public componentDidMount() {
    window.addEventListener("message", this.messageEvtListener);
    if(localStorage['github.AuthResult']) {
      try {
        this.processLoginResult(JSON.parse(localStorage['github.AuthResult']));
        localStorage.removeItem("github.AuthResult");
      } catch(ex) {
        console.error("error parsing auth result from localstorage: ", ex);
      }
    }
    else if(localStorage['github.AuthInfo']) {
      try {
        let authInfo = JSON.parse(localStorage['github.AuthInfo']);
        this.loadAuthInfo(authInfo);
      } catch(ex) {
        console.error("error parsing auth info from localstorage: ", ex);
      }
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("message", this.messageEvtListener);
    this.loginPopop = null;
  }

	public render(): React.ReactElement {
    return this.state.authInfo ? this.renderLoginState() : this.renderLoginButton();
	}

  private renderLoginButton(): React.ReactElement {
    return (
      <div className={'github-auth-box github-auth-logged-out' + (this.state.popupOpen ? ' github-auth-pending' : '')} role="button" tabIndex={0} onClick={(evt) => this.onLoginClick()} onKeyDown={(evt) => { if(evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); this.onLoginClick(); } }}>
        <div className='github-auth-btn'>
          <div className='github-auth-btn-icon'>
            <div className='logo logo-github'></div>
          </div>
          <span className='github-auth-btn-label'>
            {this.state.popupOpen ? "Waiting for GitHub..." : "Sign in with GitHub"}
          </span>
          {this.state.popupOpen ?
            <span className='github-auth-btn-spinner'></span>
          : null}
        </div>
        <div className='github-auth-hint'>Required to request tokens</div>
      </div>
    );
  }

  private renderLoginState(): React.ReactElement {
    return (
      <div className='github-auth-box github-auth-logged-in'>
        <div className='github-auth-check'>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7.5L5.5 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/></svg>
        </div>
        <div className='github-auth-profile'>
          {this.state.authInfo.avatar ?
            <img className='github-auth-avatar' src={this.state.authInfo.avatar} alt="" />
          :
            <div className='github-auth-avatar-placeholder'>
              <div className='logo logo-github'></div>
            </div>
          }
          <div className='github-auth-user-info'>
            <a className='github-auth-username' href={this.state.authInfo.url} target="_blank" rel='noopener noreferrer'>{this.state.authInfo.user}</a>
            <span className='github-auth-label'>GitHub verified</span>
          </div>
        </div>
        <button className='github-auth-signout' onClick={(evt) => {evt.stopPropagation(); this.onLogoutClick()}} title="Sign out">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>
        </button>
      </div>
    );
  }

  public getToken(): string {
    return this.state.authInfo?.token;
  }
  
  private onLoginClick() {
    let oauthState = crypto.randomUUID();
    sessionStorage.setItem("github.oauthState", oauthState);
    let authUrl = "https://github.com/login/oauth/authorize?" + toQuery({
      client_id: this.props.faucetConfig.modules.github.clientId,
      redirect_uri: this.props.faucetConfig.modules.github.redirectUrl || this.props.faucetContext.faucetApi.getApiUrl("/githubCallback", true),
      state: oauthState,
    });
    this.loginPopop = window.open(authUrl, 'github-oauth-authorize', toQuery({
      height: 800,
      width: 600
    }, ","));

    if(!this.state.popupOpen) {
      this.setState({
        popupOpen: true,
      }, () => {
        this.pollPopupState();
      });
    }
  }
  
  private onLogoutClick() {
    localStorage.removeItem("github.AuthInfo");
    this.setState({
      authInfo: null,
    });
  }


  private pollPopupState() {
    if(!this.loginPopop)
      return;
    
    if(!this.state.popupOpen)
      return;
    if(this.loginPopop.closed) {
      this.setState({
        popupOpen: false,
      });
      this.loginPopop = null;
    }
    else {
      setTimeout(() => this.pollPopupState(), 1000);
    }
  }

  private processWindowMessage(evt: MessageEvent) {
    if(evt.origin !== location.origin)
      return;
    if(!evt.data || typeof evt.data !== "object" || evt.data.authModule !== "github" || !evt.data.authResult)
      return;
    let expectedState = sessionStorage.getItem("github.oauthState");
    if(!expectedState || evt.data.authState !== expectedState)
      return;
    sessionStorage.removeItem("github.oauthState");
    this.processLoginResult(evt.data.authResult);
  }

  private processLoginResult(authResult: any) {
    console.log("github auth: ", authResult);
    if(this.loginPopop)
      this.loginPopop.close();
    if(authResult.data) {
      this.loadAuthInfo(authResult.data);
      localStorage['github.AuthInfo'] = JSON.stringify(authResult.data);
    }
  }

  private loadAuthInfo(authInfo: IGithubAuthInfo) {
    let authTimeout = this.props.faucetConfig.modules.github?.authTimeout;
    let age = this.props.faucetContext.faucetApi.getFaucetTime().getSyncedTime() - authInfo.time;
    if(age > authTimeout - 60)
      return;

    this.setState({
      authInfo: authInfo,
    });
  }

}
